import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, UserRound, PhoneCall, Mail, ThumbsUp, Newspaper, BookOpen, PlusCircle, Settings, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { RentalRulesDialog } from './RentalRulesDialog';
import { SpeedLimitQuickAction } from './SpeedLimitQuickAction';
import { BikeDetails } from '@/hooks/useBikeDetails';

interface QuickActionsCardProps {
  bikeDetails?: BikeDetails | null;
  onBikeUpdate?: () => void;
}

export function QuickActionsCard({ bikeDetails, onBikeUpdate }: QuickActionsCardProps) {
  const { user } = useAuth();
  const [isRulesDialogOpen, setIsRulesDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<{[key: string]: boolean}>({});
  const navigate = useNavigate();
  
  console.log('QuickActionsCard - bikeDetails:', bikeDetails);
  console.log('QuickActionsCard - onBikeUpdate:', typeof onBikeUpdate);
  console.log('Рендер QuickActionsCard, isRulesDialogOpen:', isRulesDialogOpen);

  const handleCallRequest = async () => {
    if (!user?.email) {
      toast.error('Пользователь не авторизован');
      return;
    }

    setIsLoading(prev => ({...prev, call: true}));

    try {
      const { data: courier, error } = await supabase
        .from('couriers')
        .select('phone')
        .eq('email', user.email)
        .single();

      if (error) throw error;

      if (!courier?.phone) {
        toast.error('Номер телефона не найден');
        return;
      }

      const phoneNumber = courier.phone.replace(/\D/g, '');
      const webhookUrl = `https://integration-webhook.mango-office.ru/webhookapp/common?code=253d5d69-ff7c-46e0-b282-1e3420bfb812&Source=Other&API_key=f7kmz2x8pn3uw8nlx409emm9z4u5vue1&Action=Callback&EmployeeNUM=750&TelNumbr=${phoneNumber}`;

      // Создаем контроллер отмены и устанавливаем таймаут
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд тайм-аут

      try {
        const response = await fetch(webhookUrl, {
          signal: controller.signal
        });
        
        // Очищаем таймаут, так как запрос выполнен
        clearTimeout(timeoutId);
        
        if (!response.ok) throw new Error('Ошибка вызова');

        toast.success('Менеджер перезвонит вам в течение нескольких минут. Пожалуйста, примите звонок.', {
          duration: 5000
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          toast.error('Превышено время ожидания ответа от сервера', {
            description: 'Попробуйте позвонить позже',
            action: {
              label: 'Попробовать снова',
              onClick: handleCallRequest
            }
          });
        } else {
          throw fetchError;
        }
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error('Не удалось совершить звонок');
    } finally {
      setIsLoading(prev => ({...prev, call: false}));
    }
  };

  const handleContactEmail = () => {
    window.location.href = 'mailto:info@tuk-tuk.rent';
    toast.success('Открываем почтовый клиент');
  };

  const handleReviewClick = () => {
    window.open('https://yandex.ru/navi/org/tuk_tuk_elektrovelosipedy/240770589273/reviews/?indoorLevel=1&ll=37.713944%2C55.807036&z=17', '_blank');
    toast.success('Переходим на страницу отзывов');
  };

  const handleNewsClick = () => {
    window.open('https://t.me/tuk_tuk_renta', '_blank');
    toast.success('Переходим в Telegram канал');
  };

  const handleRulesClick = () => {
    console.log('Открываем диалог с правилами аренды');
    setIsRulesDialogOpen(true);
  };

  const handleNewRequest = () => {
    console.log('Переходим к выбору тарифа');
    navigate('/courier/tariff');
    toast.success('Открываем страницу выбора тарифа');
  };

  const handleMySimClick = () => {
    console.log('Переходим на страницу MySim');
    navigate('/courier/mysim');
    toast.success('Переходим на страницу "Мой СИМ"');
  };

  return (
    <Card className="w-full bg-card/80 shadow-md">
      <CardHeader>
        <CardTitle className="text-lg">Быстрые действия</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {/* Кнопка управления ограничением скорости - занимает 2 колонки для максимальной видимости */}
          <SpeedLimitQuickAction 
            bikeDetails={bikeDetails} 
            onUpdate={onBikeUpdate}
          />
          
          {/* Зеленая кнопка "Новая заявка" */}
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2 bg-green-500 hover:bg-green-600 text-white border-green-600"
            onClick={handleNewRequest}
          >
            <PlusCircle className="h-5 w-5 text-white" />
            <span className="text-xs">Новая заявка</span>
          </Button>
          
          {/* Кнопка "Мой СИМ" */}
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2" 
            onClick={handleMySimClick}
          >
            <Settings className="h-5 w-5" />
            <span className="text-xs">Мой СИМ</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={handleCallRequest}
            disabled={isLoading.call}
          >
            {isLoading.call ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <PhoneCall className="h-5 w-5" />
            )}
            <span className="text-xs">{isLoading.call ? 'Вызов...' : 'Позвонить'}</span>
          </Button>
          
          <Button asChild variant="outline" className="flex flex-col h-20 gap-1 p-2">
            <Link to="/courier/profile">
              <UserRound className="h-5 w-5" />
              <span className="text-xs">Профиль</span>
            </Link>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={() => {
              window.open('https://t.me/electrocourier_bot', '_blank');
              toast.success('Открываем чат поддержки');
            }}
          >
            <MessageCircle className="h-5 w-5" />
            <span className="text-xs">Чат</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={handleContactEmail}
          >
            <Mail className="h-5 w-5" />
            <span className="text-xs">Связаться</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={handleReviewClick}
          >
            <ThumbsUp className="h-5 w-5" />
            <span className="text-xs">Отзыв</span>
          </Button>

          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={handleRulesClick}
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-xs">Договор оферты</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex flex-col h-20 gap-1 p-2"
            onClick={handleNewsClick}
          >
            <Newspaper className="h-5 w-5" />
            <span className="text-xs">Новости</span>
          </Button>
        </div>
      </CardContent>
      
      <RentalRulesDialog 
        isOpen={isRulesDialogOpen} 
        onClose={() => setIsRulesDialogOpen(false)} 
      />
    </Card>
  );
}
